/*
 * argile-projets.js — Page de liste des réalisations.
 *
 * Filtres par tag (tiroir, facettes groupées par famille) + tri + « Voir la suite ».
 * Tout est déjà rendu dans le DOM par Liquid ; le JS ne fait que basculer la
 * visibilité et réordonner. Aucun appel réseau — un métaobjet n'est pas une
 * collection paginable, donc rien à demander au serveur. Même parti pris que
 * argile-stores.js.
 *
 * ⚠️ Les facettes portent le HANDLE du tag, jamais son libellé : un libellé est
 * traduit, et filtrer dessus donnerait des résultats différents selon la langue.
 *
 * ── COMBINAISON DES FILTRES : OU, PAS ET ────────────────────────────────────────
 * Cocher « Maison » et « Paris » montre les projets qui sont l'un OU l'autre, pas
 * l'intersection. C'est la convention déjà retenue sur les facettes du catalogue, et
 * sur un corpus de quelques dizaines de projets l'intersection viderait l'écran au
 * deuxième clic. Pour passer en ET : remplacer `.some(` par `.every(` dans `visible`.
 */
(() => {
  const norm = (s) =>
    (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .trim();

  const init = (racine) => {
    const controls = racine.querySelector('[data-projets]');
    const grille = racine.querySelector('[data-projets-grid]');
    if (!controls || !grille || controls.dataset.initialise === '1') return;
    controls.dataset.initialise = '1';

    const pageSize = Math.max(1, parseInt(controls.dataset.pageSize, 10) || 12);
    const items = Array.from(grille.querySelectorAll('[data-projet-item]'));
    const vide = racine.querySelector('[data-projets-empty]');
    const loadMore = racine.querySelector('[data-projets-loadmore]');
    const boutonPlus = racine.querySelector('[data-projets-more]');
    const compteurAffiche = racine.querySelector('[data-projets-shown]');
    const compteurTotal = racine.querySelector('[data-projets-total]');

    const tiroir = racine.querySelector('[data-projets-drawer]');
    const ouvrir = racine.querySelector('[data-projets-open-filters]');
    const fermer = racine.querySelectorAll('[data-projets-close-filters]');
    const effacer = racine.querySelector('[data-projets-clear]');
    const cases = Array.from(racine.querySelectorAll('[data-projets-facet]'));
    const bulles = Array.from(racine.querySelectorAll('[data-projets-filter-count]'));
    const menuTri = racine.querySelector('[data-projets-sort]');

    let limite = pageSize;
    let tri = 'defaut';

    const tagsDe = (el) => (el.dataset.tags || '').split(/\s+/).filter(Boolean);
    const coches = () => cases.filter((c) => c.checked).map((c) => c.value);

    const visible = (el) => {
      const actifs = coches();
      if (!actifs.length) return true;
      const t = tagsDe(el);
      return actifs.some((h) => t.includes(h));
    };

    const trier = (liste) => {
      const nombre = (el, cle) => parseInt(el.dataset[cle], 10) || 0;
      const copie = [...liste];
      if (tri === 'annee-desc') copie.sort((a, b) => nombre(b, 'annee') - nombre(a, 'annee'));
      else if (tri === 'titre-asc') copie.sort((a, b) => norm(a.dataset.titre).localeCompare(norm(b.dataset.titre)));
      else if (tri === 'titre-desc') copie.sort((a, b) => norm(b.dataset.titre).localeCompare(norm(a.dataset.titre)));
      else copie.sort((a, b) => nombre(a, 'rang') - nombre(b, 'rang'));
      return copie;
    };

    const rendre = () => {
      const retenus = trier(items.filter(visible));

      // Réordonner le DOM plutôt que jouer sur `order` en CSS : l'ordre du DOM est
      // aussi celui de la tabulation et celui qu'un lecteur d'écran annonce.
      retenus.forEach((el) => grille.appendChild(el));
      items.filter((el) => !retenus.includes(el)).forEach((el) => grille.appendChild(el));

      items.forEach((el) => el.classList.add('is-hidden'));
      retenus.slice(0, limite).forEach((el, i) => {
        el.classList.remove('is-hidden');
        // L'alternance gauche/droite se recalcule sur les seules rangées VISIBLES :
        // `:nth-child(even)` compterait aussi les rangées masquées par un filtre et
        // laisserait deux images du même côté à la suite.
        el.classList.toggle('is-alt', i % 2 === 1);
      });

      const nbVisibles = Math.min(limite, retenus.length);
      if (vide) vide.hidden = retenus.length !== 0;
      if (compteurAffiche) compteurAffiche.textContent = String(nbVisibles);
      if (compteurTotal) compteurTotal.textContent = String(retenus.length);
      if (loadMore) loadMore.hidden = retenus.length <= limite;

      const n = coches().length;
      bulles.forEach((b) => {
        b.textContent = n ? String(n) : '';
        b.hidden = n === 0;
      });

      // Compte par facette : combien de projets porteraient CE tag, indépendamment
      // des autres cases cochées. Un compteur qui tomberait à zéro dès qu'un filtre
      // est actif n'aiderait personne à choisir le suivant.
      cases.forEach((c) => {
        const cible = racine.querySelector(`[data-projets-facet-count="${CSS.escape(c.value)}"]`);
        if (cible) cible.textContent = String(items.filter((el) => tagsDe(el).includes(c.value)).length);
      });
    };

    cases.forEach((c) =>
      c.addEventListener('change', () => {
        limite = pageSize; // un changement de filtre repart de la première page
        rendre();
      })
    );

    effacer?.addEventListener('click', () => {
      cases.forEach((c) => { c.checked = false; });
      limite = pageSize;
      rendre();
    });

    // « Effacer » par famille, comme sur les Points de vente. La famille est portée
    // par `data-famille` sur la case, et non déduite de la position dans le DOM :
    // les blocs sont rendus conditionnellement (une famille sans tag ne sort pas),
    // donc aucun index n'est stable.
    racine.querySelectorAll('[data-projets-facet-clear]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const famille = btn.getAttribute('data-projets-facet-clear');
        cases.forEach((c) => {
          if (c.dataset.famille === famille) c.checked = false;
        });
        limite = pageSize;
        rendre();
      });
    });

    boutonPlus?.addEventListener('click', () => {
      limite += pageSize;
      rendre();
    });

    ouvrir?.addEventListener('click', () => tiroir?.showModal());
    fermer.forEach((b) => b.addEventListener('click', () => tiroir?.close()));

    // Fermeture au clic hors du panneau. Un `<dialog>` ne le fait pas tout seul :
    // Échap ferme, le voile non. Les clics sur le voile ont pour cible le `<dialog>`
    // LUI-MÊME — le contenu est dans `__modal-inner`, qui le remplit entièrement.
    // Comparer `e.target` au dialogue suffit donc, sans calcul de coordonnées.
    tiroir?.addEventListener('click', (e) => {
      if (e.target === tiroir) tiroir.close();
    });

    menuTri?.querySelectorAll('[data-sort]').forEach((b) =>
      b.addEventListener('click', () => {
        tri = b.dataset.sort;
        menuTri.querySelectorAll('[data-sort]').forEach((x) => x.setAttribute('aria-checked', String(x === b)));
        menuTri.removeAttribute('open');
        limite = pageSize;
        rendre();
      })
    );

    // Arrivée depuis une pastille de tag (`/pages/projets?tag=salon`) : la facette
    // correspondante est pré-cochée, sinon le lien mènerait à la liste complète et
    // le clic sur la pastille n'aurait servi à rien.
    const demandes = new URLSearchParams(window.location.search).getAll('tag');
    if (demandes.length) {
      cases.forEach((c) => { c.checked = demandes.includes(c.value); });
    }

    rendre();
  };

  const initTout = (portee = document) => portee.querySelectorAll('.argile-projets').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initTout());
  } else {
    initTout();
  }

  document.addEventListener('shopify:section:load', (e) => initTout(e.target));
})();
